/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { Link, Popover, Typography } from '@one-thd/sui-atomic-components';
import proptypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import classNames from 'classnames';
import { GroupAccordionContext } from '../GroupAccordionContext';
import { useSelectAll } from '../../hooks/useSelectAll';
import { useScrollDirection } from '../../hooks/useScrollDirection';
import { useRef } from 'react';

const { func, bool, string } = proptypes;

const StickyActionBar = ({
  stickyRenderActionButtons,
  onSelectAllGroupsAndItems,
  onCollapseAll,
  onExpandAll,
  stickyActionBarToBottom,
  enableHeaderOffset,
  errorSelectionMessage,
}) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const { shouldShowStickyHeader } = useScrollDirection();
  
  // Determine if we need to offset the sticky bar for the header nav
  // Only on mobile when stickyActionBarToBottom is true, enableHeaderOffset is true, and header is visible
  const shouldOffsetForHeader = enableHeaderOffset && isMobile && stickyActionBarToBottom && shouldShowStickyHeader;
  
  const stickyContainerClass = classNames(
    'sui-bg-primary', 'sui-border-1', 'sui-border-primary',
    'sui-rounded-md', 'sui-border-solid', 'sui-px-4',
    'sui-z-50', 'sui-shadow-2xl',
    'sui-sticky', 'sui-pb-2', 'sui-pt-2',
    'sui-transition-[bottom]', 'sui-duration-300', // Smooth transition
    {
      'sui-bottom-20': !stickyActionBarToBottom && !shouldOffsetForHeader
    },
    {
      'sui-bottom-28': !isMobile && !stickyActionBarToBottom,
      'sui-w-full': !isMobile,
      'sui-w-[98%]': isMobile,
      'sui-mx-auto': isMobile
    },
    {
      'sui-bottom-0': stickyActionBarToBottom && !shouldOffsetForHeader
    },
    {
      // When header nav is visible on mobile, offset by header height
      'sui-bottom-20': shouldOffsetForHeader
    });

  // List Actions popover state
  const [popoverEl, setPopoverEl] = useState(null);
  // Reference to observe scroll position when List Actions is opened
  const popoverOpenScrollPosition = useRef(null)

  const {
    handleSelectAll,
    handleUnselectAll,
    handleCollapseAll,
    handleExpandAll,
    allExpanded,
    isAllSelected
  } = useSelectAll({ onSelectAllGroupsAndItems, onCollapseAll, onExpandAll });

  const handleClick = (event) => {
    popoverOpenScrollPosition.current = window.scrollY;
    setPopoverEl(event.currentTarget);
  };

  const handleClose = () => {
    setPopoverEl(null);
    popoverOpenScrollPosition.current = null;
  };
  const open = Boolean(popoverEl);
  const id = open ? 'simple-popover' : undefined;

  const { selectedItems } = useContext(GroupAccordionContext);
  const numberOfSelectedItems = useMemo(() => {
    return Object.keys(selectedItems).reduce((acc, groupId) => {
      return {
        // Count the selected items in the group
        itemCount: acc.itemCount + selectedItems[groupId].items.length,
        // Count the group if the group is selected
        groupCount: acc.groupCount + (selectedItems[groupId].isSelected ? 1 : 0)
      };
    }, {
      itemCount: 0,
      groupCount: 0
    });
  }, [selectedItems]);
  const groupText = numberOfSelectedItems.groupCount === 1 ? 'group' : 'groups';
  const itemText = numberOfSelectedItems.itemCount === 1 ? 'item' : 'items';

  /**
   * Function to close List Actions when the user scrolls the page to avoid UI bugs
   */
  const closePopoverOnScroll = useCallback(() => {
    if (popoverOpenScrollPosition.current !== null && popoverEl !== null && Math.abs(window.scrollY - popoverOpenScrollPosition.current) >= 100) {
      handleClose();
    }
  }, [popoverEl, handleClose]);

  /**
   * Effect to observe scroll events and close the List Actions popover if the user scrolls more than 100px
   */
  useEffect(() => {
    if (window) {
      window.addEventListener('scroll', closePopoverOnScroll, { passive: true });

      // Clean up the event listener when popover closes or component unmounts
      return () => {
        window.removeEventListener('scroll', closePopoverOnScroll);
      };
    }
  }, [closePopoverOnScroll]);

  return (
    <div className={stickyContainerClass} data-testid="sticky-action-bar">
      <div className="sui-mb-1 sui-flex sui-flex-row sui-gap-2">
        <Typography
          data-testid="sticky-action-bar-group-count"
          weight="bold"
        >{numberOfSelectedItems.groupCount} {groupText} selected
        </Typography>
        <Typography
          data-testid="sticky-action-bar-item-count"
        >| {numberOfSelectedItems.itemCount} {itemText} selected
        </Typography>
      </div>
      <div className="sui-flex sui-justify-between sui-items-center">
        { !isMobile && (
          <div>
            <Typography
              component="span"
              tabIndex="0"
              role="button"
              decoration="underline"
              onClick={handleClick}
            >
              List Actions
            </Typography>
            <Popover
              id={id}
              open={open}
              anchorEl={popoverEl}
              onClose={handleClose}
              title="List Actions"
            >
              <div className="sui-flex sui-flex-col sui-gap-6">
                <Link
                  component="button"
                  onClick={isAllSelected ? handleUnselectAll : handleSelectAll}
                >
                  {isAllSelected ? 'Unselect All' : 'Select All'}
                </Link>
                <Link
                  component="button"
                  onClick={allExpanded ? handleCollapseAll : handleExpandAll}
                >
                  {allExpanded ? 'Collapse All' : 'Expand All'}
                </Link>
              </div>
            </Popover>
          </div>
        )}
        {stickyRenderActionButtons({ selectedItems })}
      </div>
      { errorSelectionMessage && (
        <div className="sui-w-full">
          <Typography color="danger">{errorSelectionMessage}</Typography>
        </div>
      ) }
    </div>
  );
};

StickyActionBar.propTypes = {
  stickyRenderActionButtons: func,
  onSelectAllGroupsAndItems: func,
  onCollapseAll: func,
  onExpandAll: func,
  errorSelectionMessage: string,
  stickyActionBarToBottom: bool,
  enableHeaderOffset: bool
};

StickyActionBar.defaultProps = {
  stickyRenderActionButtons: () => {},
  onSelectAllGroupsAndItems: () => {},
  onCollapseAll: () => {},
  onExpandAll: () => {},
  errorSelectionMessage: null,
  stickyActionBarToBottom: false,
  enableHeaderOffset: false
};

StickyActionBar.displayName = 'StickyActionBar';

StickyActionBar.dataModel = {};

export { StickyActionBar };
